// Add at the top of Talkpage.js
import React, { useState } from 'react';
import { Modal, Button, Form, Row, Col, InputGroup, Badge } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { addNewTalkAction } from '../../utils/redux/talks/talksAction';

// Add this component inside Talkpage.js (outside the main Talkpage function)
const AddTalkModal = ({ show, handleClose, categories, onSuccess }) => {
    const dispatch = useDispatch();
    const [form, setForm] = useState({
        title: '',
        talkDate: '',
        durationHhmmss: '',
        tags: '',
        keywords: '',
        category: '',
        talkLink: '',
        summary: '',
        summaryLink: '',
        description: '',
    });
    const [errors, setErrors] = useState({});

    const validate = () => {
        const errs = {};
        if (!form.title.trim()) errs.title = 'Title is required';
        if (!form.talkDate) errs.talkDate = 'Talk year is required';
        if (!form.durationHhmmss) errs.durationHhmmss = 'Duration is required';
        return errs;
    };

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errs = validate();
        setErrors(errs);
        if (Object.keys(errs).length > 0) return;

        // Prepare data
        const data = {
            ...form,
            tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
            keywords: form.keywords ? form.keywords.split(',').map(k => k.trim()).filter(Boolean) : [],
        };
        const res = await dispatch(addNewTalkAction(data));
        if (res?.success) {
            handleClose();
            setForm({
                title: '',
                talkDate: '',
                durationHhmmss: '',
                tags: '',
                keywords: '',
                category: '',
                talkLink: '',
                summary: '',
                summaryLink: '',
                description: '',
            });
            if (onSuccess) onSuccess();
        }
    };

    return (
        <Modal show={show} onHide={handleClose} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>Add New Talk</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={handleSubmit}>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Title <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    name="title"
                                    value={form.title}
                                    onChange={handleChange}
                                    isInvalid={!!errors.title}
                                    placeholder="Talk Title"
                                />
                                <Form.Control.Feedback type="invalid">{errors.title}</Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group className="mb-3">
                                <Form.Label>Talk Year <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="date"
                                    name="talkDate"
                                    value={form.talkDate}
                                    onChange={handleChange}
                                    isInvalid={!!errors.talkDate}
                                />
                                <Form.Control.Feedback type="invalid">{errors.talkDate}</Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group className="mb-3">
                                <Form.Label>Duration (hh:mm:ss) <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    name="durationHhmmss"
                                    value={form.durationHhmmss}
                                    onChange={handleChange}
                                    isInvalid={!!errors.durationHhmmss}
                                    placeholder="e.g. 01:30:00"
                                />
                                <Form.Control.Feedback type="invalid">{errors.durationHhmmss}</Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Tags <small>(comma separated)</small></Form.Label>
                                <Form.Control
                                    name="tags"
                                    value={form.tags}
                                    onChange={handleChange}
                                    placeholder="e.g. React, Node, MongoDB"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Keywords <small>(comma separated)</small></Form.Label>
                                <Form.Control
                                    name="keywords"
                                    value={form.keywords}
                                    onChange={handleChange}
                                    placeholder="e.g. frontend, backend"
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Category</Form.Label>
                                <Form.Select
                                    name="category"
                                    value={form.category}
                                    onChange={handleChange}
                                >
                                    <option value="">Select Category</option>
                                    {categories?.map(cat => (
                                        <option key={cat._id} value={cat._id}>{cat.name}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Talk Link</Form.Label>
                                <Form.Control
                                    name="talkLink"
                                    value={form.talkLink}
                                    onChange={handleChange}
                                    placeholder="https://..."
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Summary Link</Form.Label>
                                <Form.Control
                                    name="summaryLink"
                                    value={form.summaryLink}
                                    onChange={handleChange}
                                    placeholder="https://..."
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Description</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    name="description"
                                    value={form.description}
                                    onChange={handleChange}
                                    placeholder="Talk description"
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Form.Group className="mb-3">
                        <Form.Label>Summary</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            name="summary"
                            value={form.summary}
                            onChange={handleChange}
                            placeholder="Summary text"
                        />
                    </Form.Group>
                    <div className="d-flex justify-content-end">
                        <Button variant="secondary" onClick={handleClose} className="me-2">
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit">
                            Add Talk
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default AddTalkModal